import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const TeacherClassNotes = ({ onNavigate, userInfo }) => {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ subjects: [], batches: [], department: '' });
    const [showUpload, setShowUpload] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [form, setForm] = useState({ title: '', description: '', subject: '', batch_name: '', department: '', year: '' });
    const [file, setFile] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };

    useEffect(() => {
        fetchNotes();
        fetchFilters();
        const handleResize = () => setIsMobile(window.innerWidth <= 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchNotes = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_LINK}/api/teacher/class-notes`, { headers });
            const r = await res.json();
            if (r.success) setNotes(r.data || []);
        } catch (e) { console.error('Error fetching notes:', e); }
        finally { setLoading(false); }
    };

    const fetchFilters = async () => {
        try {
            const res = await fetch(`${API_LINK}/api/teacher/class-notes/filters`, { headers });
            const r = await res.json();
            if (r.success) {
                const data = r.data || { subjects: [], batches: [], department: '' };
                setFilters(data);
                if (data.department) {
                    setForm(f => ({ ...f, department: data.department }));
                }
            }
        } catch (e) { console.error('Error fetching filters:', e); }
    };

    const handleUpload = async () => {
        if (!form.title || !form.subject || !form.batch_name || !file) {
            return alert('Please fill title, select subject, batch and choose a file');
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('noteFile', file);
            fd.append('title', form.title);
            fd.append('description', form.description);
            fd.append('subject', form.subject);
            fd.append('batch_name', form.batch_name);
            fd.append('department', form.department);
            fd.append('year', form.year);

            const res = await fetch(`${API_LINK}/api/teacher/class-notes`, {
                method: 'POST', headers, body: fd
            });
            const r = await res.json();
            if (r.success) {
                alert('Class note uploaded successfully!');
                setShowUpload(false);
                setForm({ title: '', description: '', subject: '', batch_name: '', department: filters.department || '', year: '' });
                setFile(null);
                fetchNotes();
            } else {
                alert(r.message || 'Upload failed');
            }
        } catch (e) { alert('Upload error: ' + e.message); }
        finally { setUploading(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this note?')) return;
        try {
            const res = await fetch(`${API_LINK}/api/teacher/class-notes/${id}`, { method: 'DELETE', headers });
            const r = await res.json();
            if (r.success) fetchNotes();
            else alert(r.message || 'Delete failed');
        } catch (e) { alert('Error deleting note'); }
    };

    const handleBatchSelect = (batchName) => {
        const batch = filters.batches.find(b => b.name === batchName);
        setForm(f => ({
            ...f,
            batch_name: batchName,
            department: batch?.department || f.department,
            year: batch?.year || f.year
        }));
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const getFileIcon = (fileName) => {
        if (!fileName) return '📄';
        const ext = fileName.split('.').pop().toLowerCase();
        const icons = { pdf: '📕', doc: '📘', docx: '📘', ppt: '📙', pptx: '📙', xls: '📗', xlsx: '📗', txt: '📝', zip: '📦', rar: '📦', jpg: '🖼️', jpeg: '🖼️', png: '🖼️' };
        return icons[ext] || '📄';
    };

    const filteredNotes = notes.filter(n =>
        !searchTerm || n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        n.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        n.batch_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const cs = {
        card: {
            background: '#fff',
            borderRadius: '10px',
            padding: isMobile ? '1rem' : '1.5rem',
            boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
            border: '1px solid #eef2f6',
            transition: 'all 0.2s'
        },
        btn: {
            padding: isMobile ? '0.5rem 1rem' : '0.6rem 1.2rem',
            border: 'none',
            borderRadius: '10px',
            fontWeight: '600',
            cursor: 'pointer',
            fontSize: isMobile ? '0.82rem' : '0.9rem',
            transition: 'all 0.2s'
        },
        input: {
            width: '100%',
            padding: isMobile ? '0.6rem 0.8rem' : '0.7rem 1rem',
            border: '2px solid #e2e8f0',
            borderRadius: '10px',
            fontSize: isMobile ? '0.9rem' : '0.95rem',
            outline: 'none',
            boxSizing: 'border-box',
            transition: 'border 0.2s'
        },
        label: {
            display: 'block',
            marginBottom: '0.4rem',
            fontWeight: '600',
            fontSize: '0.85rem',
            color: '#475569'
        },
        badge: {
            padding: '0.2rem 0.6rem',
            borderRadius: '20px',
            fontSize: '0.72rem',
            fontWeight: '600',
            display: 'inline-block'
        }
    };

    return (
        <Layout role="teacher" onNavigate={onNavigate} activePage="teacher-class-notes" userInfo={userInfo}>
            {/* Header Card */}
            <div style={{
                background: 'linear-gradient(135deg, #238548, #1a6b38)',
                borderRadius: '10px',
                padding: isMobile ? '1.2rem' : '1.5rem 2rem',
                marginBottom: '1.5rem',
                boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'flex-start' : 'center',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '1rem' : '0'
            }}>
                <div>
                    <h1 style={{
                        margin: 0,
                        fontSize: isMobile ? '1.3rem' : '1.6rem',
                        fontWeight: '700',
                        color: '#fff'
                    }}>
                        📒 Class Notes
                    </h1>
                    <p style={{ margin: '0.3rem 0 0', color: 'rgba(255,255,255,0.8)', fontSize: isMobile ? '0.82rem' : '0.9rem' }}>
                        Upload and manage class notes for your department
                        {filters.department && <span style={{ fontWeight: '600', color: '#fff' }}> — {filters.department}</span>}
                    </p>
                </div>
                <button
                    onClick={() => setShowUpload(true)}
                    style={{
                        ...cs.btn,
                        background: '#fff',
                        color: '#238548',
                        padding: isMobile ? '0.55rem 1.1rem' : '0.7rem 1.5rem',
                        fontSize: isMobile ? '0.85rem' : '1rem',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        whiteSpace: 'nowrap',
                        alignSelf: isMobile ? 'flex-start' : 'center'
                    }}
                >
                    ➕ Upload New Note
                </button>
            </div>

            {/* Search Bar */}
            <div style={{ marginBottom: '1.5rem' }}>
                <input
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="🔍 Search notes by title, subject, or batch..."
                    style={{
                        ...cs.input,
                        maxWidth: isMobile ? '100%' : '500px',
                        background: '#fff',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                    }}
                />
            </div>

            {/* Stats */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: isMobile ? '0.75rem' : '1rem',
                marginBottom: '1.5rem'
            }}>
                {[
                    { label: 'Total Notes', value: notes.length, icon: '📒', bg: '#eff6ff', color: '#2563eb' },
                    { label: 'Subjects', value: [...new Set(notes.map(n => n.subject))].length, icon: '📖', bg: '#f0fdf4', color: '#16a34a' },
                    { label: 'Batches', value: [...new Set(notes.map(n => n.batch_name))].length, icon: '👥', bg: '#fefce8', color: '#ca8a04' },
                    { label: 'Downloads', value: notes.reduce((a, n) => a + (n.download_count || 0), 0), icon: '⬇️', bg: '#faf5ff', color: '#9333ea' }
                ].map((s, i) => (
                    <div key={i} style={{
                        ...cs.card,
                        background: s.bg,
                        borderLeft: `4px solid ${s.color}`,
                        padding: isMobile ? '0.75rem 1rem' : '1rem 1.2rem'
                    }}>
                        <div style={{ fontSize: isMobile ? '1.3rem' : '1.5rem', marginBottom: '0.3rem' }}>{s.icon}</div>
                        <div style={{ fontSize: isMobile ? '1.2rem' : '1.4rem', fontWeight: '700', color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: isMobile ? '0.72rem' : '0.8rem', color: '#64748b', fontWeight: '500' }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {/* Notes List */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                    <div style={{
                        width: 40, height: 40,
                        border: '4px solid #e2e8f0',
                        borderTop: '4px solid #238548',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 1rem'
                    }} />
                    Loading class notes...
                </div>
            ) : filteredNotes.length === 0 ? (
                <div style={{ ...cs.card, textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                    <h3 style={{ margin: 0, color: '#475569' }}>{searchTerm ? 'No matching notes found' : 'No Class Notes Yet'}</h3>
                    <p style={{ margin: '0.5rem 0 0' }}>{searchTerm ? 'Try a different search term' : 'Click "Upload New Note" to get started!'}</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                    {filteredNotes.map(note => (
                        <div key={note.id} style={{
                            ...cs.card,
                            display: 'flex',
                            alignItems: isMobile ? 'flex-start' : 'center',
                            flexDirection: isMobile ? 'column' : 'row',
                            gap: isMobile ? '0.8rem' : '1.2rem'
                        }}
                            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.12)'}
                            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.06)'}>

                            {/* Top row on mobile: icon + title + actions */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.8rem',
                                width: '100%'
                            }}>
                                {/* File Icon */}
                                <div style={{
                                    width: isMobile ? 44 : 50,
                                    height: isMobile ? 44 : 50,
                                    borderRadius: '12px',
                                    background: '#f1f5f9',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: isMobile ? '1.4rem' : '1.6rem',
                                    flexShrink: 0
                                }}>
                                    {getFileIcon(note.file_name)}
                                </div>

                                {/* Note Info */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                        fontWeight: '600',
                                        fontSize: isMobile ? '0.95rem' : '1.05rem',
                                        color: '#1e293b',
                                        marginBottom: '0.2rem',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>{note.title}</div>
                                    {note.description && (
                                        <div style={{
                                            fontSize: '0.82rem',
                                            color: '#64748b',
                                            marginBottom: '0.2rem',
                                            display: isMobile ? 'none' : 'block'
                                        }}>{note.description}</div>
                                    )}
                                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                        <span style={{ ...cs.badge, background: '#dbeafe', color: '#2563eb' }}>📖 {note.subject}</span>
                                        <span style={{ ...cs.badge, background: '#dcfce7', color: '#16a34a' }}>👥 {note.batch_name}</span>
                                        {note.department && !isMobile && (
                                            <span style={{ ...cs.badge, background: '#fef3c7', color: '#b45309' }}>🏛️ {note.department}</span>
                                        )}
                                    </div>
                                </div>

                                {/* Actions — shown inline on mobile */}
                                {isMobile && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', flexShrink: 0 }}>
                                        <a href={`${API_LINK}/${note.file_path}`} target="_blank" rel="noreferrer"
                                            style={{ ...cs.btn, background: '#eff6ff', color: '#2563eb', textDecoration: 'none', fontSize: '0.78rem', padding: '0.4rem 0.7rem' }}>
                                            👁️ View
                                        </a>
                                        <button onClick={() => handleDelete(note.id)}
                                            style={{ ...cs.btn, background: '#fef2f2', color: '#dc2626', fontSize: '0.78rem', padding: '0.4rem 0.7rem' }}>
                                            🗑️
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Meta + Actions row — desktop layout */}
                            {!isMobile && (
                                <>
                                    <div style={{ textAlign: 'right', minWidth: '120px', flexShrink: 0 }}>
                                        <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: '0.2rem' }}>{formatFileSize(note.file_size)}</div>
                                        <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: '0.2rem' }}>⬇️ {note.download_count || 0} downloads</div>
                                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{new Date(note.created_at).toLocaleDateString('en-IN')}</div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                                        <a href={`${API_LINK}/${note.file_path}`} target="_blank" rel="noreferrer"
                                            style={{ ...cs.btn, background: '#eff6ff', color: '#2563eb', textDecoration: 'none', fontSize: '0.82rem', padding: '0.45rem 0.8rem' }}>
                                            👁️ View
                                        </a>
                                        <button onClick={() => handleDelete(note.id)}
                                            style={{ ...cs.btn, background: '#fef2f2', color: '#dc2626', fontSize: '0.82rem', padding: '0.45rem 0.8rem' }}>
                                            🗑️ Delete
                                        </button>
                                    </div>
                                </>
                            )}

                            {/* Mobile: meta info row */}
                            {isMobile && (
                                <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', alignItems: 'center', width: '100%', paddingTop: '0.2rem', borderTop: '1px solid #f1f5f9' }}>
                                    <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{formatFileSize(note.file_size)}</span>
                                    <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>⬇️ {note.download_count || 0} downloads</span>
                                    <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{new Date(note.created_at).toLocaleDateString('en-IN')}</span>
                                    {note.department && (
                                        <span style={{ ...cs.badge, background: '#fef3c7', color: '#b45309' }}>🏛️ {note.department}</span>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Upload Modal */}
            {showUpload && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 9999, backdropFilter: 'blur(4px)',
                    padding: isMobile ? '1rem' : '0'
                }}>
                    <div style={{
                        background: '#fff',
                        borderRadius: '14px',
                        padding: isMobile ? '1.2rem' : '2rem',
                        width: '95%',
                        maxWidth: '550px',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: '0 25px 60px rgba(0,0,0,0.2)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h2 style={{ margin: 0, fontSize: isMobile ? '1.1rem' : '1.3rem', color: '#1e293b' }}>📒 Upload Class Note</h2>
                            <button onClick={() => setShowUpload(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
                        </div>

                        {filters.department && (
                            <div style={{ marginBottom: '1rem', padding: '0.5rem 1rem', background: '#f0fdf4', borderRadius: '8px', fontSize: '0.85rem', color: '#16a34a', fontWeight: '500' }}>
                                🏛️ Department: <strong>{filters.department}</strong>
                            </div>
                        )}

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {/* Title */}
                            <div>
                                <label style={cs.label}>Title *</label>
                                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                                    style={cs.input} placeholder="e.g. Unit 1 - Introduction to Algorithms" />
                            </div>

                            {/* Description */}
                            <div>
                                <label style={cs.label}>Description (optional)</label>
                                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                    style={{ ...cs.input, resize: 'vertical' }} rows={2} placeholder="Brief description of the notes" />
                            </div>

                            {/* Subject */}
                            <div>
                                <label style={cs.label}>Subject *</label>
                                {filters.subjects.length > 0 ? (
                                    <select value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} style={cs.input}>
                                        <option value="">-- Select Subject --</option>
                                        {filters.subjects.map((s, i) => (
                                            <option key={i} value={s.code}>{s.name} ({s.code})</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                                        style={cs.input} placeholder="Enter subject code or name" />
                                )}
                            </div>

                            {/* Batch */}
                            <div>
                                <label style={cs.label}>Batch *</label>
                                {filters.batches.length > 0 ? (
                                    <select value={form.batch_name} onChange={e => handleBatchSelect(e.target.value)} style={cs.input}>
                                        <option value="">-- Select Batch --</option>
                                        {filters.batches.map((b, i) => (
                                            <option key={i} value={b.name}>{b.name} ({b.department} - {b.year})</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input value={form.batch_name} onChange={e => setForm(f => ({ ...f, batch_name: e.target.value }))}
                                        style={cs.input} placeholder="Enter batch name" />
                                )}
                            </div>

                            {/* File */}
                            <div>
                                <label style={cs.label}>File * (PDF, DOC, PPT, XLS, TXT, ZIP, Images — max 20MB)</label>
                                <input type="file"
                                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.zip,.rar,.jpg,.jpeg,.png"
                                    onChange={e => setFile(e.target.files[0])}
                                    style={cs.input} />
                                {file && (
                                    <div style={{ marginTop: '0.4rem', fontSize: '0.82rem', color: '#16a34a', fontWeight: 500 }}>
                                        ✅ {file.name} ({formatFileSize(file.size)})
                                    </div>
                                )}
                            </div>

                            {/* Actions */}
                            <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'flex-end', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                                <button onClick={() => setShowUpload(false)}
                                    style={{ ...cs.btn, background: '#f1f5f9', color: '#475569', flex: isMobile ? '1' : 'unset' }}>Cancel</button>
                                <button onClick={handleUpload} disabled={uploading}
                                    style={{ ...cs.btn, background: 'linear-gradient(135deg, #238548, #16a34a)', color: '#fff', opacity: uploading ? 0.7 : 1, boxShadow: '0 4px 15px rgba(35,133,72,0.3)', flex: isMobile ? '1' : 'unset' }}>
                                    {uploading ? '⏳ Uploading...' : '📤 Upload Note'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </Layout>
    );
};

export default TeacherClassNotes;
